import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { TrendingUp, Star, Globe, Target } from "lucide-react";

interface TipsterCardProps {
  tipster: {
    id: string;
    name: string;
    avatar?: string;
    country: string;
    roi: number;
    winRate: number;
    streak: number;
    totalTips: number;
    rating: number;
    sports: string[];
    isPremium?: boolean;
  };
}

export function TipsterCard({ tipster }: TipsterCardProps) {
  const getRoiColor = (roi: number) => {
    if (roi > 0) return "text-profit";
    if (roi < 0) return "text-loss";
    return "text-muted-foreground";
  };

  return (
    <Card className="bg-gradient-card border-border/50 hover:border-primary/30 transition-all duration-300 hover:shadow-card backdrop-blur-sm">
      <CardContent className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <Avatar className="h-12 w-12 ring-2 ring-border">
              <AvatarImage src={tipster.avatar} alt={tipster.name} />
              <AvatarFallback className="bg-primary/10 text-foreground font-semibold">
                {tipster.name.split(' ').map(n => n[0]).join('').toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-foreground">{tipster.name}</h3>
                {tipster.isPremium && (
                  <Badge variant="outline" className="bg-gradient-premium border-premium/30 text-premium-foreground text-xs">
                    VIP
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                <Globe className="h-3 w-3" />
                <span>{tipster.country}</span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-1">
            <Star fill="currentColor" className="h-4 w-4 text-premium" />
            <span className="text-sm font-medium text-foreground">{tipster.rating}</span>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="bg-background/50 rounded-lg p-3 border border-border/30">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="h-4 w-4 text-profit" />
              <span className="text-xs text-muted-foreground">ROI</span>
            </div>
            <div className={`text-lg font-bold ${getRoiColor(tipster.roi)}`}>
              {tipster.roi > 0 ? '+' : ''}{tipster.roi}%
            </div>
          </div>
          
          <div className="bg-background/50 rounded-lg p-3 border border-border/30">
            <div className="flex items-center gap-2 mb-1">
              <Target className="h-4 w-4 text-primary" />
              <span className="text-xs text-muted-foreground">Win Rate</span>
            </div>
            <div className="text-lg font-bold text-foreground">
              {tipster.winRate}%
            </div>
          </div>
        </div>

        {/* Additional Stats */}
        <div className="flex justify-between text-sm mb-4">
          <div>
            <span className="text-muted-foreground">Streak: </span>
            <span className={`font-medium ${tipster.streak > 0 ? 'text-profit' : 'text-loss'}`}>
              {tipster.streak > 0 ? '+' : ''}{tipster.streak}
            </span>
          </div>
          <div>
            <span className="text-muted-foreground">Tips: </span>
            <span className="font-medium text-foreground">{tipster.totalTips}</span>
          </div>
        </div>

        {/* Sports Tags */}
        <div className="flex flex-wrap gap-2 mb-4">
          {tipster.sports.slice(0, 3).map((sport) => (
            <Badge key={sport} variant="secondary" className="text-xs bg-secondary/50">
              {sport}
            </Badge>
          ))}
          {tipster.sports.length > 3 && (
            <Badge variant="secondary" className="text-xs bg-secondary/50">
              +{tipster.sports.length - 3}
            </Badge>
          )}
        </div>

        {/* Action Button */}
        <Button 
          className="w-full" 
          variant={tipster.isPremium ? "premium" : "betting"}
        >
          View Profile
        </Button>
      </CardContent>
    </Card>
  );
}